import { type CSSProperties, memo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { type GetByShortIDCollectionResponse } from '@air/api';
import { type ShortUrlResponse } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';
import { cn } from '@air/utils-tailwind';

import { usePublicPermissions } from '~/components/PublicPermissionsProvider';
import {
  ScrollElementContextConsumer,
  ScrollElementContextProvider,
} from '~/components/v2/components/shared/scroll/ScrollElementContextProvider';
import { getShareLinkPublicColorMode } from '~/components/v2/components/shared/share-link-design/shareLinkColorMode';
import { getShareLinkPageMaxWidthStyle } from '~/components/v2/components/shared/share-link-design/shareLinkPageWidth';
import {
  getGalleryCardRadiusStyle,
  getShareLinkPageBottomCornerClass,
  getShareLinkPageCornerClass,
} from '~/components/v2/components/shared/share-link-design/shareLinkThumbnailRadius';
import { usePublicShowcaseCardSizeSync } from '~/components/v2/components/showcase/hooks/usePublicShowcaseCardSizeSync';
import { SHOWCASE_SCROLLPORT_PROPS } from '~/components/v2/components/showcase/hooks/usePublicShowcaseHeaderScrollOpacity';
import { usePublicShowcaseImmersiveCover } from '~/components/v2/components/showcase/hooks/usePublicShowcaseImmersiveCover';
import { PublicShowcaseBoardView } from '~/components/v2/components/showcase/PublicShowcaseBoardView';
import { PublicShowcaseBottomBar } from '~/components/v2/components/showcase/PublicShowcaseBottomBar';
import { PublicShowcaseContent } from '~/components/v2/components/showcase/PublicShowcaseContent';
import { PublicShowcaseCover } from '~/components/v2/components/showcase/PublicShowcaseCover';
import {
  PublicShowcaseHeader,
  type PublicShowcaseHeaderProps,
} from '~/components/v2/components/showcase/PublicShowcaseHeader';
import { PublicShowcaseNav } from '~/components/v2/components/showcase/PublicShowcaseNav';
import {
  PublicShowcasePageSeoTitle,
  type PublicShowcasePageSeoTitleProps,
} from '~/components/v2/components/showcase/PublicShowcasePageSeoTitle';
import { PublicShowcaseSubPageBreadcrumbs } from '~/components/v2/components/showcase/PublicShowcaseSubPageBreadcrumbs';
import { PublicShowcaseWorkspaceHeader } from '~/components/v2/components/showcase/PublicShowcaseWorkspaceHeader';
import { SHOWCASE_PAGE_COLUMN_CLASS_NAME } from '~/components/v2/components/showcase/showcasePageColumn';
import { usePublicShareLinkForcedTheme } from '~/components/v2/store/features/share-link-theme/usePublicShareLinkForcedTheme';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useBodyBackground } from '~/hooks/useBodyBackground';
import { useCollectionPermissionsCache } from '~/hooks/useCollectionPermissionsCache';
import { usePublicBoardlessConfigView } from '~/hooks/usePublicBoardlessConfigView/usePublicBoardlessConfigView';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { getPublicHeadlineFontFamilyValue, usePublicHeadlineFont } from '~/hooks/usePublicHeadlineFont';
import { usePublicHeadlineFontLoader } from '~/hooks/usePublicHeadlineFontLoader';
import { MouseSelectionProvider } from '~/providers/MouseSelectionProvider';
import { usePublicShowcaseContext } from '~/providers/PublicShowcaseProvider';
import { ScrollElementProvider } from '~/providers/ScrollElementProvider';
import { resetCentralizedBoardAction } from '~/store/centralizedBoard/actions';

export type PublicShowcasePageProps = {
  // The subset this page and its direct children consume — composed from the children's own prop
  // types (child-relative Picks) plus this page's `collection.id` read for the permissions cache —
  // so surfaces without the full public record (Preview renders from the private collection fetch)
  // can supply exactly what the tree renders.
  collection: Pick<GetByShortIDCollectionResponse['data'], 'id'> &
    PublicShowcasePageSeoTitleProps['collection'] &
    PublicShowcaseHeaderProps['collection'];
  presentationSettings?: ShortUrlResponse['presentationSettings'];
};

// React's CSSProperties has no index signature for custom properties, so the var must be named
// explicitly for the style object to typecheck without an assertion (precedent:
// shareLinkThumbnailRadius's gallery-card radius vars).
// The weight/style vars are optional because they only exist when the stored font names a variant;
// without one the surfaces keep the weights they hardcoded before the vars existed (their fallbacks).
type ShowcaseHeadlineFontVarStyle = CSSProperties & {
  '--headline-font-family': string;
  '--headline-font-weight'?: string;
  '--headline-font-style'?: string;
};

export const PublicShowcasePage = memo(({ collection, presentationSettings }: PublicShowcasePageProps) => {
  const { shortId } = useShortIdContext();
  const dispatch = useDispatch();
  const { permissions } = usePublicPermissions();
  const { setCollectionPermissions } = useCollectionPermissionsCache();
  const { pageBgColor } = usePublicCustomColors();

  // `SelectionBar` is the surface's multi-select bar, composed at the bottom of the page.
  // `activeBoardId` is the board drilled into from this collection, rendered below in place of the
  // collection page body; null when the collection's own body is showing. The supplying surface
  // decides how the id resolves (the `/b/{boardId}` URL segment vs the collection link editor's state).
  const { activeBoardId: drilledBoardId, SelectionBar } = usePublicShowcaseContext();

  // Paint the <body> with the resolved page background so overscroll behind the scroll container
  // shows the page color, not the default white body. Falls back to the same theme-aware token the
  // page wrapper's `bg-gray-4` resolves to (never hardcoded, so it stays correct in dark theme).
  useBodyBackground(pageBgColor ?? 'var(--colors-gray-4)');

  // Force the document theme to the link's Page theme setting while this page is mounted. Absent
  // colorMode (legacy links) is Light — the same default the editor shows — so a visitor's
  // workspace/OS theme cannot leak in. `'toggle'` still leaves the visitor in charge. Edit does
  // not mount this page (`ShowcaseEditorPage` + `data-theme` on the canvas instead).
  usePublicShareLinkForcedTheme(getShareLinkPublicColorMode(presentationSettings?.colorMode));

  const headlineFont = usePublicHeadlineFont();
  // Injects the <head> tag that actually loads the family (Google Fonts link or @font-face);
  // no-op when the link has no headline font.
  usePublicHeadlineFontLoader();

  const scrollElementRef = useRef<HTMLDivElement | HTMLElement | null>(null);

  const pageId = `collection-${shortId}`;
  const { PublicBoardlessConfigObserver } = usePublicBoardlessConfigView({
    pageId,
    presentationDefaultView: presentationSettings?.defaultView,
  });
  usePublicShowcaseCardSizeSync();

  useEffect(() => {
    dispatch(resetCentralizedBoardAction());
  }, [dispatch]);

  // Load permissions into Redux store
  useEffect(() => {
    if (permissions) {
      setCollectionPermissions({ collectionId: collection.id, permissions });
    }
  }, [collection.id, permissions, setCollectionPermissions]);

  // FOUND-584: an active search renders the search results page — its OWN page, composed as one
  // whole stack (see `pageBody` below) rather than per-element gates. The workspace header and
  // the page root stay ABOVE that decision on purpose: the link's brand (custom colors, fonts,
  // theme, pageWidth) deliberately survives into the results page.
  const { isSearchActive } = useIsSearchActive();

  // Whether an immersive cover is showing — the layout this page composes below (full-bleed cover
  // above the column, scroll area pulled up under the header) and the treatment the workspace header
  // applies to its own surface are the SAME state, so both read it from one hook rather than each
  // deriving it (the header's own copy used to disagree; see usePublicShowcaseImmersiveCover).
  const { hasImmersiveCover } = usePublicShowcaseImmersiveCover();

  const pageMaxWidthStyle = getShareLinkPageMaxWidthStyle(presentationSettings?.pageWidth);
  // `undefined` for the default radius, so a legacy link's page root style stays byte-identical.
  const galleryCardRadiusStyle = getGalleryCardRadiusStyle(presentationSettings?.thumbnailRadius);
  // `undefined` when the link has no headline font, so the shared gallery titles (which read the
  // vars with their pre-var fallbacks) — and this root's style attr — stay byte-identical to today.
  // The weight/style vars publish the chosen variant so every headline surface APPLIES the one face
  // the loader requested; without a variant they stay unset and the surfaces keep their own weights.
  const headlineFontVarStyle: ShowcaseHeadlineFontVarStyle | undefined = headlineFont
    ? {
        '--headline-font-family': getPublicHeadlineFontFamilyValue(headlineFont.fontFamily),
        ...(headlineFont.fontWeight ? { '--headline-font-weight': String(headlineFont.fontWeight) } : undefined),
        ...(headlineFont.fontStyle ? { '--headline-font-style': headlineFont.fontStyle } : undefined),
      }
    : undefined;

  // The un-drilled body of <main>: ONE of two stacks, decided once here (a variable rather than a
  // third JSX branch under the drill ternary — noNestedTernary). Composing whole stacks instead of
  // per-element `!isSearchActive` gates means chrome added to the showcase stack can never leak
  // onto the results page by forgetting its own gate.
  const pageBody = isSearchActive ? (
    // FOUND-584: the search results page — the flat gallery of matching items
    // (PublicShowcaseContent swaps to it on its own search read) in the same column box, with
    // the bottom bar and NONE of the showcase chrome (cover, page nav, breadcrumbs, heading).
    // `hasImmersiveCover` is false here (it is search-aware), so the workspace header renders in
    // normal colored flow over this stack.
    <div
      className={cn(
        SHOWCASE_PAGE_COLUMN_CLASS_NAME,
        'flex min-h-full flex-col ads:bg-base bg-gray-1',
        getShareLinkPageCornerClass(presentationSettings?.thumbnailRadius),
        pageBgColor && 'mt-2',
      )}
    >
      <PublicShowcaseContent />
      {PublicBoardlessConfigObserver}
      <div
        className={cn(
          'sticky bottom-0 z-10 mt-auto overflow-hidden',
          getShareLinkPageBottomCornerClass(presentationSettings?.thumbnailRadius),
        )}
      >
        <PublicShowcaseBottomBar />
      </div>
    </div>
  ) : (
    // The showcase: cover, sub-page nav, breadcrumbs, page heading, content, bottom bar.
    <>
      {/* Immersive cover slot: a direct child of the full-width scroller, so the cover spans the
          browser edge-to-edge, bleeds under the transparent header (the -mt-18 pull-under below),
          and scrolls away with the content. */}
      {hasImmersiveCover && <PublicShowcaseCover />}
      <div
        className={cn(
          SHOWCASE_PAGE_COLUMN_CLASS_NAME,
          'flex min-h-full flex-col ads:bg-base bg-gray-1',
          // Column corners follow the link's Corner rounding setting (rounded-12 by default).
          getShareLinkPageCornerClass(presentationSettings?.thumbnailRadius),
          !hasImmersiveCover && pageBgColor && 'mt-2',
        )}
      >
        {/* Standard cover slot: the cover scrolls with the column and sizes relative to it —
            fullWidth is flush to the column edges, inset pads itself in. */}
        {!hasImmersiveCover && <PublicShowcaseCover />}
        {/* Sub-page navigation sits below the cover and above the page header, sticky to the
            scrollport top so it stays reachable once the cover scrolls away (prototype parity).
            In the immersive case the cover is a direct child of <main> above this column, so the
            nav is still the first thing below it. Renders nothing when the link has no sub-pages,
            keeping a legacy link's column byte-identical. Desktop only — the mobile menu lives in
            the workspace header. */}
        <PublicShowcaseNav />
        {/* On a sub-page, `Home / {Page Name}` sits above the page header; the root page renders
            no breadcrumbs (unchanged). */}
        <PublicShowcaseSubPageBreadcrumbs />
        <PublicShowcaseHeader collection={collection} />
        <PublicShowcaseContent />
        {PublicBoardlessConfigObserver}
        {/* The bottom bar lives INSIDE the column so it inherits the column's box — positioning
            it in the frame instead re-derives "100%" from the frame (scrollbar included) while
            the column resolves it from <main>'s content box (scrollbar excluded), overhanging the
            column's right edge by one scrollbar-width when classic scrollbars are on. Sticky pins
            the bar to the scrollport bottom while scrolling (content slides under its translucent
            blur) and it rests at the column's end at scroll bottom, where the wrapper's
            rounded-b-12 clip matches the column's own corners. `mt-auto` (with the column's
            flex-col) keeps it at the viewport bottom for short content; the bar always renders at
            h-12, reserving its own height in flow, so the column needs no bottom padding. The
            clip's bottom corners follow the Corner rounding setting alongside the column's own. */}
        <div
          className={cn(
            'sticky bottom-0 z-10 mt-auto overflow-hidden',
            getShareLinkPageBottomCornerClass(presentationSettings?.thumbnailRadius),
          )}
        >
          <PublicShowcaseBottomBar />
        </div>
      </div>
    </>
  );

  return (
    // The page root hosts --share-link-page-max-width (and the gallery card radius +
    // headline-font-family vars when the link customizes those) so every consumer of
    // SHOWCASE_PAGE_COLUMN_CLASS_NAME — the workspace header's constrained content, the cover's
    // "inset" wrapper, and the content column inside <main> — and every gallery card inside the
    // column reads the same vars.
    <div
      data-testid="PUBLIC_COLLECTION_PAGE_ROOT"
      // h-screen is the document-owning contract (the parent chain never guarantees a height).
      className="flex size-full h-screen flex-col overflow-hidden ads:bg-ads-gray-4 bg-gray-4"
      style={{
        ...pageMaxWidthStyle,
        ...galleryCardRadiusStyle,
        ...headlineFontVarStyle,
        ...(pageBgColor ? { backgroundColor: pageBgColor } : undefined),
      }}
    >
      {hasImmersiveCover ? (
        // The header still reserves its 72px of flow space (the scroll area below is pulled up
        // under it via -mt-18); this wrapper lifts it above the scroll container so it overlays
        // the cover, starting transparent and fading its surface in as the cover scrolls away
        // (see PublicShowcaseWorkspaceHeader).
        <div className="relative z-20 shrink-0">
          <PublicShowcaseWorkspaceHeader />
        </div>
      ) : (
        <PublicShowcaseWorkspaceHeader />
      )}
      {/* Unpadded flex host: <main> reaches the viewport edges so the scrollbar sits at the
          absolute right edge of the page, not floated inward by a gutter. The page color shows
          beside the width-constrained gray-1 column because the scroller is transparent; the
          custom-page-color top gap lives on the column itself (mt-2, below) so it scrolls away
          naturally. Immersive pulls the whole scroll area up under the transparent header. */}
      <div className={cn('flex grow overflow-hidden', hasImmersiveCover && '-mt-18')}>
        <PublicShowcasePageSeoTitle collection={collection} />
        {/* The scroller below is also the selection area (`#main`) — a plain click anywhere inside
            it clears the current selection (see useSelectedItemsMouseSelection). */}
        <MouseSelectionProvider selectionArea={scrollElementRef}>
          <ScrollElementProvider scrollElementRef={scrollElementRef}>
            <ScrollElementContextProvider>
              <ScrollElementContextConsumer>
                {({ setScrollElement }) => (
                  // Transparent frame: the full-width scroller (<main>) lives inside it, so the page
                  // background shows through around the white column.
                  <div className="grow overflow-hidden">
                    {/* <main> is a full-width transparent scroller reaching the viewport edges, so
                      the scrollbar sits at the absolute right edge of the page over the page
                      background (prototype parity) and drag-select spans the whole frame, page-bg
                      gutters included. SHOWCASE_SCROLLPORT_PROPS marks it as the scrollport the
                      workspace header's fade tracks (usePublicShowcaseHeaderScrollOpacity). The gray-1 surface is the
                      width-constrained column INSIDE the scroll content: `min-h-full` keeps the
                      scroll range surface-colored end to end (the page background only peeks past
                      the bounds during native overscroll bounce), the sticky bottom bar rides
                      inside the column as its last flex child, and the custom-page-color `mt-2`
                      top gap scrolls away with the content. The gallery's
                      resize-detector measures the column div — its nearest block ancestor — so the
                      max-width still fully constrains content. Immersive is the full-bleed COVER
                      variant, not a full-bleed page: the cover renders as a direct child of
                      <main> above the column, spanning the scroller edge-to-edge under the
                      transparent header, while the column keeps its normal max-width, rounding,
                      and surface below it — it only skips the custom-page-color top gap because
                      the cover occupies the top and the column sits flush beneath it. The gray-1
                      surface is composed on the column rather than baked into
                      SHOWCASE_PAGE_COLUMN_CLASS_NAME because that class is shared with the
                      workspace header's content container, which must stay surface-less. */}
                    <main
                      data-testid="PUBLIC_COLLECTION_LAYOUT_MAIN"
                      className="h-full overflow-y-auto"
                      {...SHOWCASE_SCROLLPORT_PROPS}
                      // biome-ignore lint/performance/noJsxPropsBind: pre-existing scrollport ref callback
                      ref={(ref) => {
                        scrollElementRef.current = ref;
                        setScrollElement(ref);
                      }}
                    >
                      {drilledBoardId ? (
                        // Viewing a board inside the collection (the public collection-board route OR
                        // the Edit tab): the board subtree renders in a white column matching the
                        // collection column — same gray-1 surface, corner rounding, and link max-width
                        // — replacing the collection cover / nav / header / content / bottom bar
                        // wholesale. It reuses THIS <main> as its scroll + selection host, so the board
                        // gallery virtualizes against the same scroller the collection page uses and
                        // the selection bar below still reflects the board's selection.
                        <div
                          className={cn(
                            SHOWCASE_PAGE_COLUMN_CLASS_NAME,
                            'flex min-h-full flex-col ads:bg-base bg-gray-1',
                            getShareLinkPageCornerClass(presentationSettings?.thumbnailRadius),
                            pageBgColor && 'mt-2',
                          )}
                        >
                          <PublicShowcaseBoardView boardId={drilledBoardId} />
                        </div>
                      ) : (
                        pageBody
                      )}
                    </main>
                  </div>
                )}
              </ScrollElementContextConsumer>
            </ScrollElementContextProvider>
            {/* The surface's own bar: the viewer surfaces supply Download/Share, the editor's Edit
                tab count + deselect + a visible "Remove from link" button. */}
            <SelectionBar />
          </ScrollElementProvider>
        </MouseSelectionProvider>
      </div>
    </div>
  );
});

PublicShowcasePage.displayName = 'PublicShowcasePage';
